import type { TopCoverMaterial } from './prescriptionState';

/**
 * Wording for the top cover material "None" (2026-11-03, Rafael), in one
 * place so the summary, the Excel lab sheet, the workshop PDF and the patient
 * page cannot disagree:
 *   - the form chip reads "None";
 *   - Material None + a layer: the ~1 mm layer is applied directly to the
 *     shell, there is no foam base -> NO_FOAM_BASE_LABEL on every output;
 *   - Material None + Layer None: no top cover at all -> SHELL_ONLY_LABEL in
 *     the summary and every supplier output.
 * Stored values are untouched: 'None' is one more TopCoverMaterial value, the
 * layer and colour keys keep their meaning, and older lines never carry it.
 * The material is configuration-only: it feeds no SKU mapping, price, Zoho
 * line, invoice matcher or thickness rule (see coverColorsFor's note).
 */
export const TOP_COVER_MATERIAL_NONE: TopCoverMaterial = 'None';
export const SHELL_ONLY_LABEL = 'No top cover (shell only)';
export const NO_FOAM_BASE_LABEL = 'None (no foam base, layer applied directly)';

/** Legacy 'Poron 3mm' reads as 'Poron'; anything else passes through. */
export function normalizeTopCoverMaterialValue(v: unknown): string {
  if (v === undefined || v === null || v === '') return '';
  return v === 'Poron 3mm' ? 'Poron' : String(v);
}

/** Material None with no layer (or Layer None): nothing on the shell. */
export function isShellOnly(
  d: { top_cover_material?: unknown; top_cover_layer?: unknown } | undefined,
): boolean {
  if (!d || d.top_cover_material !== TOP_COVER_MATERIAL_NONE) return false;
  return !d.top_cover_layer || d.top_cover_layer === 'None';
}

/**
 * The material as the summary and every supplier-facing output print it:
 * the stored label for EVA / Poron, the two sentences above for None.
 */
export function topCoverMaterialOutputLabel(material: unknown, layer: unknown): string {
  const m = normalizeTopCoverMaterialValue(material);
  if (m !== TOP_COVER_MATERIAL_NONE) return m;
  return !layer || layer === 'None' ? SHELL_ONLY_LABEL : NO_FOAM_BASE_LABEL;
}
